import { useCallback } from "react";

import type { NodeId } from "~/models/dto";
import { getTime } from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";
import { isPresent } from "~/tools/is-present";

import type { TimeRange } from "../app/state/view/timeline/TimeRange";
import {
	type Api,
	type Argument,
	type DeleteMonitorError,
	type GetMonitorCheckStatesParams,
	type Monitor,
	type MonitorCheckStatus,
	type MonitorCheckStatusHealthHistory,
	type MonitorCheckStatusRelatedFailures,
	type MonitorOverviewList,
	type MonitorPreviewResult,
	type MonitorStatus,
	type MonitorStatusValue,
	type TestMonitorFunctionCheckStatesParams,
	type ViewCheckState,
	deleteMonitorFn,
	getMonitorCheckStatesFn,
	getMonitorCheckStatusFn,
	getMonitorCheckStatusHealthHistoryFn,
	getMonitorCheckStatusRelatedFailuresFn,
	getMonitorFn,
	getMonitorWithStatusFn,
	getMonitorsOverviewFn,
	patchMonitorFn,
	testMonitorFunctionCheckStatesFn,
	testMonitorFunctionFn,
} from "../common/openapi";

import {
	type InvalidateFunction,
	type UseFullQueryOptions,
	type UseMutationOptions,
	type UseMutationResult,
	type UseQueryOptions,
	type UseQueryResult,
	useMutation,
	useQuery,
	useQueryClient,
} from "./react-query";

export const useQueryMonitorsOverview = <TResult = MonitorOverviewList>(
	options?: UseQueryOptions<MonitorOverviewList, Error, TResult>,
): UseQueryResult<TResult, Error> => useQuery(["monitorsOverview"], () => getMonitorsOverviewFn({}), options);

export const useInvalidateMonitorOverviewQuery = (): InvalidateFunction => {
	const queryClient = useQueryClient();

	return useCallback(() => queryClient.invalidateQueries([{}, "monitorsOverview"]), [queryClient]);
};

export const useQueryMonitor = <TResult = Monitor>(
	monitorIdOrUrn: string | number,
	options?: UseQueryOptions<Monitor, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(["monitor", monitorIdOrUrn], () => getMonitorFn({ monitorIdOrUrn: String(monitorIdOrUrn) }), {
		refetchOnWindowFocus: false,
		...options,
	});

export interface QueryMonitorStatusParams {
	monitorIdOrUrn: string | number | NodeId;
	timestamp?: Time;
}

export const queryMonitorStatus = <TResult = MonitorStatus>(
	{ monitorIdOrUrn, timestamp }: QueryMonitorStatusParams,
	options?: UseQueryOptions<MonitorStatus, Error, TResult>,
): UseFullQueryOptions<MonitorStatus, Error, TResult> => ({
	refetchOnWindowFocus: false,
	...options,
	timestamp,
	queryKey: ["monitor", "status", monitorIdOrUrn],
	queryFn: () =>
		getMonitorWithStatusFn({
			monitorIdOrUrn: String(monitorIdOrUrn),
			timestamp: isPresent(timestamp) ? getTime(timestamp) : undefined,
		}),
});

export const useInvalidateMonitorStatusQuery = (): InvalidateFunction => {
	const queryClient = useQueryClient();

	return useCallback(() => queryClient.invalidateQueries([{}, "monitor", "status"]), [queryClient]);
};

export const queryMonitorCheckStatus = ({
	identifier,
	timestamp,
}: {
	identifier: string;
	timestamp: Time | undefined;
}) =>
	({
		timestamp,
		queryKey: ["monitor", "checkStatus", identifier],
		queryFn: () => getMonitorCheckStatusFn({ identifier, topologyTime: getTime(timestamp ?? Date.now()) }),
		refetchOnWindowFocus: false,
	}) as const satisfies UseFullQueryOptions<MonitorCheckStatus, Error>;

export function queryMonitorCheckStatusHealthHistory({
	identifier,
	timeRange,
}: {
	identifier: string;
	timeRange: TimeRange;
}) {
	return {
		queryKey: ["monitor", "checkStatus", "healthHistory", identifier],
		queryFn: () =>
			getMonitorCheckStatusHealthHistoryFn({
				identifier,
				startTime: timeRange.start().valueOf(),
				endTime: timeRange.endDate().valueOf(),
			}),
		/**
		 * Timeline should update within interval in live mode.
		 * If data stays the same, react-query will reuse reference to the data and the timeline will not update.
		 * So we disable structural sharing to force react-query to always return new data.
		 */
		structuralSharing: false,
		timeRange,
	} as const satisfies UseFullQueryOptions<MonitorCheckStatusHealthHistory, Error>;
}

export const useQueryMonitorCheckStatusRelatedFailures = <TResult = MonitorCheckStatusRelatedFailures>(
	{ identifier, timestamp }: { identifier: string; timestamp: Time | undefined },
	options?: UseQueryOptions<MonitorCheckStatusRelatedFailures, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(
		["monitor", "checkStatus", "relatedFailure", identifier],
		() => getMonitorCheckStatusRelatedFailuresFn({ identifier, topologyTime: getTime(timestamp ?? Date.now()) }),
		{ refetchOnWindowFocus: false, ...options, timestamp },
	);

export const queryTestMonitorFunction = <TResult = MonitorPreviewResult>(
	{
		monitorFunctionIdOrUrn,
		arguments: args,
	}: { monitorFunctionIdOrUrn: NodeId | number | string; arguments: ReadonlyArray<Argument> },
	options?: UseQueryOptions<MonitorPreviewResult, Error, TResult>,
): UseFullQueryOptions<MonitorPreviewResult, Error, TResult> => ({
	...options,
	queryKey: ["monitor", monitorFunctionIdOrUrn, "test", args],
	queryFn: () =>
		testMonitorFunctionFn({
			monitorFunctionIdOrUrn: String(monitorFunctionIdOrUrn),
			body: { arguments: args.slice() },
		}),
});

export const useUpdateMonitorStatus = (
	options?: UseMutationOptions<Monitor, Error, { monitorId: string | number; status: MonitorStatusValue }>,
): UseMutationResult<Monitor, Error, { monitorId: string | number; status: MonitorStatusValue }> =>
	useMutation(
		({ monitorId, status }) => patchMonitorFn({ monitorIdOrUrn: String(monitorId), body: { status } }),
		options,
	);

export const useDeleteMonitor = (
	options?: UseMutationOptions<void, DeleteMonitorError, string | number>,
): UseMutationResult<void, DeleteMonitorError, string | number> =>
	useMutation((monitorIdOrUrn) => deleteMonitorFn({ monitorIdOrUrn: String(monitorIdOrUrn) }), options);

export interface QueryMonitorCheckStatesParams
	extends Omit<GetMonitorCheckStatesParams, "monitorIdOrUrn" | "timestamp"> {
	monitorIdOrUrn: number | string;
	timestamp?: Time;
}

export const queryMonitorCheckStates = <TResult = ReadonlyArray<ViewCheckState>>(
	{ monitorIdOrUrn, limit, healthState, timestamp }: QueryMonitorCheckStatesParams,
	options?: UseQueryOptions<ReadonlyArray<ViewCheckState>, Error, TResult>,
): UseFullQueryOptions<ReadonlyArray<ViewCheckState>, Error, TResult> => ({
	...options,
	timestamp,
	queryKey: ["monitor", "check-states", monitorIdOrUrn, { limit, healthState }],
	queryFn: async () => {
		const { states } = await getMonitorCheckStatesFn({
			monitorIdOrUrn: String(monitorIdOrUrn),
			limit,
			healthState,
			timestamp: isPresent(timestamp) ? getTime(timestamp) : undefined,
		});

		return states;
	},
});

export interface QueryTestMonitorCheckStatesParams
	extends Omit<TestMonitorFunctionCheckStatesParams, "body" | "monitorFunctionIdOrUrn"> {
	monitorFunctionIdOrUrn: number | string;
	arguments: ReadonlyArray<Api.Argument>;
}

export const queryTestMonitorCheckStates = <TResult = ReadonlyArray<ViewCheckState>>(
	{ monitorFunctionIdOrUrn, limit, healthState, arguments: args }: QueryTestMonitorCheckStatesParams,
	options?: UseQueryOptions<ReadonlyArray<ViewCheckState>, Error, TResult>,
): UseFullQueryOptions<ReadonlyArray<ViewCheckState>, Error, TResult> => ({
	...options,
	queryKey: ["monitor", "check-states", monitorFunctionIdOrUrn, args, { limit, healthState }],
	queryFn: async () => {
		const { states } = await testMonitorFunctionCheckStatesFn({
			monitorFunctionIdOrUrn: String(monitorFunctionIdOrUrn),
			limit,
			healthState,
			body: { arguments: args.slice() },
		});

		return states;
	},
});
